import classNames from 'classnames';
import React, { useContext, useState, useReducer, useEffect, useCallback, useMemo } from 'react';
import { BearerTokenGetterContext } from '../../context/BearerTokenGetterContext.js';
import { ErrorHandlerContext } from '../../context/ErrorHandlerContext.js';
import { MiniDialogModalTranslationsContext } from '../../context/MiniDialogModalTranslationsContext.js';
import { getMiniDialog, getSignedUrls } from './util/mini-dialog-api.js';
import { Button } from '../button/index.js';
import { ModalDialog, Dialog } from '../dialog/index.js';
import { MediaLinkDialogHeader } from '../media-link-dialog-header/index.js';
import { MiniDialogLine } from './components/mini-dialog-line.js';
import { ToggleTextButton } from './components/toggle-text-button.js';
import { actorReducer } from './actor-reducer.js';
import { useAudioRecorder } from '../../hooks/useAudioRecorder.js';
import useMediaPlayer from '../../hooks/useMediaPlayer.js';
import styles from './mini-dialog.module.css.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const PlayMode = {
    DIALOG: 'DIALOG',
    SINGLE_LINE: 'SINGLE_LINE',
};
const Notification = notification => {
    return (React.createElement("div", { className: 'bc-notification__banner bc-notification__banner--warning' },
        React.createElement("i", { className: 'bc-icon bc-icon-info bc-icon--left' }),
        React.createElement("div", { dangerouslySetInnerHTML: { __html: notification } })));
};
function MiniDialogModal({ mediaLink, superModuleHasExerciseEntrance, hasCompactHeader, onClose, urlPrefix, studioGroup, }) {
    const translations = useContext(MiniDialogModalTranslationsContext);
    const handleError = useContext(ErrorHandlerContext);
    const getBearerToken = useContext(BearerTokenGetterContext);
    const [keywordsHidden, setKeywordsHidden] = useState(false);
    const [miniDialog, setMiniDialog] = useState();
    const [urlData, setUrlData] = useState();
    const [currentPlayMode, setCurrentPlayMode] = useState(PlayMode.DIALOG);
    const [actorsByDialogOrder, setActorsByDialogOrder] = useState();
    const [mediaPlayerRef, { initialize, urlLoaded, play, pause, stop, mute, unmute, status: mediaPlayerStatus }] = useMediaPlayer(handleError);
    const [actors, dispatch] = useReducer(actorReducer, {});
    useEffect(() => {
        async function getMiniDialogData() {
            const token = await getBearerToken();
            const { id: medialinkId, moduleId, miniDialog: { id: miniDialogId }, } = mediaLink;
            try {
                const response = await getMiniDialog(token, moduleId, medialinkId, miniDialogId, studioGroup);
                setMiniDialog(response);
            }
            catch (error) {
                handleError(error);
            }
        }
        getMiniDialogData();
    }, [getBearerToken, mediaLink, handleError, studioGroup]);
    useEffect(() => {
        if (!miniDialog)
            return;
        const usedActors = miniDialog.actors.filter(actor => miniDialog.lines.find(line => line.actorId === actor.actorId));
        setActorsByDialogOrder(usedActors.map(actor => actor.actorId));
        dispatch({ type: 'INITIALIZE_ACTORS', payload: usedActors });
    }, [miniDialog]);
    useEffect(() => {
        async function getSignedUrlData(lines) {
            const token = await getBearerToken();
            const fileIds = lines.map((line) => line.audioFile).filter(x => x);
            try {
                const data = await getSignedUrls(token, fileIds);
                const sortedByDialog = {};
                fileIds.forEach(fileId => (sortedByDialog[fileId] = data[fileId]));
                setUrlData(sortedByDialog);
            }
            catch (error) {
                handleError(error);
            }
        }
        if (!miniDialog)
            return;
        const { lines } = miniDialog;
        getSignedUrlData(lines);
    }, [getBearerToken, miniDialog, handleError]);
    const initializeAsMP3 = useCallback((urls) => initialize(urls, 'mp3'), [initialize]);
    useEffect(() => {
        if (!urlData)
            return;
        initializeAsMP3(Object.values(urlData));
    }, [initializeAsMP3, urlData]);
    const actorForAudioUrl = useMemo(() => {
        if (!miniDialog || !urlData)
            return;
        return miniDialog.lines.reduce((actorForAudioUrl, line) => {
            if (line.audioFile) {
                const fileUrl = urlData[line.audioFile];
                actorForAudioUrl[fileUrl] = line.actorId;
            }
            return actorForAudioUrl;
        }, {});
    }, [miniDialog, urlData]);
    useEffect(() => {
        if (!actorForAudioUrl || !urlLoaded || !actors || mediaPlayerStatus !== 'playing')
            return;
        const actorId = actorForAudioUrl[urlLoaded];
        const actor = actors[actorId];
        if (!actor)
            return;
        if (actor.mute)
            mute();
        else
            unmute();
    }, [urlLoaded, mute, unmute, actors, actorForAudioUrl, mediaPlayerStatus]);
    const playAll = useCallback(() => {
        if (!urlData)
            return;
        setCurrentPlayMode(PlayMode.DIALOG);
        initializeAsMP3(Object.values(urlData));
        play();
    }, [setCurrentPlayMode, initializeAsMP3, play, urlData]);
    const playSingle = useCallback((audioUrl) => {
        if (currentPlayMode === PlayMode.DIALOG) {
            setCurrentPlayMode(PlayMode.SINGLE_LINE);
            initializeAsMP3([audioUrl]);
        }
        else if (audioUrl !== urlLoaded) {
            initializeAsMP3([audioUrl]);
        }
        play();
    }, [currentPlayMode, initializeAsMP3, setCurrentPlayMode, urlLoaded, play]);
    const { start: startRecording, status: audioRecorderStatus, stop: stopRecording } = useAudioRecorder(handleError);
    const denyRecordingRights = useMemo(() => audioRecorderStatus !== 'idle', [audioRecorderStatus]);
    const handleRecording = useCallback(async () => {
        if (urlData && Object.keys(urlData).length) {
            stop();
        }
        const started = await startRecording();
        if (started) {
            return {
                stopRecording: async () => {
                    const recording = await stopRecording();
                    return recording;
                },
            };
        }
        return undefined;
    }, [urlData, startRecording, stop, stopRecording]);
    const dialogPlaying = currentPlayMode === PlayMode.DIALOG && mediaPlayerStatus === 'playing';
    const denyPlayRights = useMemo(() => audioRecorderStatus === 'recording' || dialogPlaying, [audioRecorderStatus, dialogPlaying]);
    return (React.createElement(ModalDialog, { id: 'mini-dialog-modal', onClose: () => onClose(), closeTitle: translations.closeDialog, closeViaEscape: !mediaLink.contentType.faqTutorial, fullScreen: true, className: styles.modal },
        React.createElement(MediaLinkDialogHeader, { mediaLink: mediaLink, superModuleHasExerciseEntrance: superModuleHasExerciseEntrance, translations: {
                generalNode: translations.generalNode,
                close: translations.closeDialog,
            }, isCompact: hasCompactHeader, studioGroup: studioGroup }),
        React.createElement(Dialog.Body, { className: styles.body },
            audioRecorderStatus === 'no-media-devices' && Notification(translations.errors.browserSupport),
            audioRecorderStatus === 'access-denied' && Notification(translations.errors.accessDenied),
            audioRecorderStatus === 'no-microphone' && Notification(translations.errors.noMicrophone),
            React.createElement("div", { className: styles.hidden },
                React.createElement("div", { ref: mediaPlayerRef, id: 'media-player' })),
            miniDialog && urlData && Object.values(actors).length > 0 && (React.createElement("div", { className: styles.container, "data-testid": 'container' },
                React.createElement("main", { className: styles.main },
                    React.createElement("table", { className: styles.table },
                        React.createElement("thead", null,
                            React.createElement("tr", { className: styles.borderBottom },
                                React.createElement("th", { colSpan: 3 }, dialogPlaying ? (React.createElement(Button, { variant: 'primary', className: styles.headerButton, onClick: stop },
                                    React.createElement("i", { className: 'bc-icon bc-icon-audio-stop bc-icon--left', role: 'img' }),
                                    React.createElement("span", null, translations.stopDialog))) : (React.createElement(Button, { variant: 'primary', disabled: audioRecorderStatus === 'recording' || Object.keys(urlData).length === 0, className: classNames(styles.headerButton, {
                                        [styles.disabled]: audioRecorderStatus === 'recording',
                                    }), onClick: playAll },
                                    React.createElement("i", { className: 'bc-icon bc-icon-audio-play bc-icon--left', role: 'img' }),
                                    React.createElement("span", null, translations.playDialog)))),
                                React.createElement("th", { className: styles.headerTitle }, translations.example),
                                React.createElement("th", { className: styles.headerTitle }, translations.ownVoice))),
                        React.createElement("tbody", null, miniDialog.lines.map(line => {
                            const { audioFile } = line;
                            const audioUrl = audioFile && urlData[audioFile];
                            return (React.createElement(MiniDialogLine, { key: line._id, actor: actors[line.actorId], text: line.text, keywordsHidden: keywordsHidden, audioUrl: audioUrl, urlLoaded: urlLoaded, playMode: currentPlayMode, mediaPlayerStatus: mediaPlayerStatus, onPlay: denyPlayRights ? undefined : playSingle, onPause: pause, onRecord: denyRecordingRights ? undefined : handleRecording, urlPrefix: urlPrefix }));
                        })))),
                React.createElement("aside", { className: styles.aside },
                    miniDialog.image && (React.createElement("div", { role: 'img', className: styles.image, style: { backgroundImage: `url('${urlPrefix || ''}/api/files/${miniDialog.image}')` } })),
                    React.createElement("div", { className: styles.optionsWrapper },
                        React.createElement("h2", { className: styles.optionsHeader }, translations.optionsTextAndSound),
                        React.createElement("ol", { className: classNames(styles.list, styles.options) },
                            actorsByDialogOrder.map((actorId) => {
                                const actor = actors[actorId];
                                return (actor && (React.createElement("li", { key: actor.name, className: classNames(styles.listItem, styles.option) },
                                    React.createElement("span", { className: styles.optionName }, actor.name),
                                    React.createElement(ToggleTextButton, { hideText: actor.hideText, hideTextTitle: translations.hideTextTitle, showTextTitle: translations.showTextTitle, onClick: () => dispatch({ type: 'TOGGLE_TEXT_ACTOR', payload: actor.actorId }) }),
                                    React.createElement("button", { type: 'button', title: actor.mute ? translations.unmuteTitle : translations.muteTitle, className: styles.optionButton, onClick: () => dispatch({ type: 'TOGGLE_MUTE_ACTOR', payload: actor.actorId }) },
                                        React.createElement("i", { className: `bc-icon bc-icon-sound${actor.mute ? '-muted' : ''}`, role: 'img' })))));
                            }),
                            React.createElement("li", { className: classNames(styles.listItem, styles.optionKeywords) },
                                React.createElement("span", { className: styles.optionName }, translations.optionsKeyWords),
                                React.createElement(ToggleTextButton, { hideText: keywordsHidden, hideTextTitle: translations.hideKeywordsTitle, showTextTitle: translations.showKeywordsTitle, onClick: () => setKeywordsHidden(hide => !hide) }))))))))));
}

export { MiniDialogModal };
